/**
 * Which register columns this browser shows, remembered between visits.
 *
 * A layout is a habit, not a decision: an operator who works the register for
 * turnover and headcount wants those columns tomorrow morning too, and re-ticking
 * them every session is a small daily tax (the same argument as
 * `_shared/density.ts`).
 *
 * Stored as the visible keys rather than the hidden ones, and read back through
 * `normalizeColumns`, which is the whole reason this is a separate, pure file:
 * the stored list is data from an earlier version of the app and cannot be
 * trusted to name columns that still exist, to keep the locked ones, or to be a
 * list at all.
 *
 * A column added to `AR_COLUMNS` later therefore arrives switched OFF for anyone
 * who has already saved a layout, even if it is a default-on column. That is the
 * deliberate reading of "your layout is yours" - the alternative, folding new
 * defaults in, would put a column back on a table somebody had tidied.
 */
export const COLUMN_STORAGE_KEY = "crm.ariregister.columns";

/**
 * A stored (or missing) column list, made safe to render from.
 *
 * - `null`/garbage/an empty result falls back to the defaults, so a corrupt
 *   value cannot leave the operator with a blank table.
 * - Unknown keys are dropped (a column that has since been removed).
 * - Locked keys are forced back in - the name column is what makes a row
 *   openable, and no stored preference may take it away.
 * - The result is ordered by `known`, not by the stored list: the column ORDER
 *   belongs to the table, so ticking a column on cannot move it.
 */
export function normalizeColumns(
  stored: unknown,
  known: string[],
  locked: string[],
  defaults: string[],
): string[] {
  const wanted = Array.isArray(stored) ? stored.filter((k): k is string => typeof k === "string") : null;
  const picked = wanted?.filter((k) => known.includes(k)) ?? [];
  const chosen = new Set(picked.length > 0 ? picked : defaults);
  for (const k of locked) chosen.add(k);
  return known.filter((k) => chosen.has(k));
}

/** Read the saved layout. Never throws - a browser with storage disabled (or a
 *  private window in Safari) simply gets the defaults. */
export function readStoredColumns(): unknown {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(COLUMN_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

/** Save the layout, or forget it when it is back to the defaults - so that a
 *  later change to what "default" means still reaches anybody who never had an
 *  opinion of their own. */
export function writeStoredColumns(keys: string[], defaults: string[]): void {
  if (typeof window === "undefined") return;
  try {
    const same = keys.length === defaults.length && keys.every((k) => defaults.includes(k));
    if (same) window.localStorage.removeItem(COLUMN_STORAGE_KEY);
    else window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // A full or disabled storage is not worth an error on a layout preference.
  }
}
